/**
 * composer-tool-trigger.tsx — Nút kích hoạt một tiện ích của ô soạn tin trên ĐIỆN THOẠI (xem lib/composer-tools.ts), hai kiểu hiển thị:
 *   'icon' — nút tròn chỉ có biểu tượng, nằm trên thanh công cụ (tiện ích được ghim);
 *   'tile' — ô vuông có biểu tượng + nhãn, nằm trong hộp "Tiện ích" (composer-tools-sheet.tsx).
 * Dùng với `asChild` của Popover / DropdownMenu nên là forwardRef và truyền nguyên các thuộc tính còn lại (onClick, disabled, aria-*…).
 */
import * as React from 'react'
import type { LucideIcon } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ToolTriggerMode = 'icon' | 'tile'

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  mode: ToolTriggerMode
  icon: LucideIcon
  label: string
  loading?: boolean
}

export const ComposerToolTrigger = React.forwardRef<HTMLButtonElement, Props>(({ mode, icon: Icon, label, loading, className, ...props }, ref) => {
  const glyph = loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />
  if (mode === 'icon') {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-muted disabled:opacity-40', className)}
        {...props}
      >
        {glyph}
      </button>
    )
  }
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className={cn('flex w-full flex-col items-center gap-1.5 text-center text-[11px] leading-tight text-foreground active:opacity-70 disabled:opacity-40', className)}
      {...props}
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">{glyph}</span>
      <span className="line-clamp-2">{label}</span>
    </button>
  )
})
ComposerToolTrigger.displayName = 'ComposerToolTrigger'
